import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, Expand } from "lucide-react";

const PHOTOS = [
  { src: "/gallery/w1.webp", alt: "Two new FuseBox consumer units installed and labelled during a full rewire" },
  { src: "/gallery/w2.webp", alt: "EV charging point installed on an exterior brick wall" },
  { src: "/gallery/w6.webp", alt: "Aims Electrical engineer installing an EV charger at a home" },
  { src: "/gallery/w7.webp", alt: "Modern kitchen with under-cabinet LED strip lighting installed by Aims Electrical" },
  { src: "/gallery/w3.webp", alt: "Aims Electrical team installing conduit and containment on a commercial fit-out" },
  { src: "/gallery/w4.webp", alt: "Aims Electrical electrician cutting cable tray with an angle grinder" },
  { src: "/gallery/w5.webp", alt: "External CCTV camera installed on a stone building" },
];

export const Slideshow = () => {
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const n = PHOTOS.length;

  const prev = useCallback(() => setIndex((i) => (i - 1 + n) % n), [n]);
  const next = useCallback(() => setIndex((i) => (i + 1) % n), [n]);

  // Auto-advance (paused while the lightbox is open)
  useEffect(() => {
    if (lightbox) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [lightbox, next]);

  // Keyboard controls for the lightbox
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, prev, next]);

  return (
    <section id="gallery" data-testid="slideshow" className="py-20 lg:py-28 border-b border-hairline">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <h2 className="font-heading uppercase leading-[0.9] tracking-tight text-4xl sm:text-6xl text-stark">
            Recent work<span className="text-brand">.</span>
          </h2>
          <div className="hidden sm:flex gap-3">
            <button onClick={prev} data-testid="slideshow-prev" aria-label="Previous photo"
              className="grid place-items-center w-12 h-12 border border-hairline text-stark hover:bg-brand hover:text-void hover:border-brand transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={next} data-testid="slideshow-next" aria-label="Next photo"
              className="grid place-items-center w-12 h-12 border border-hairline text-stark hover:bg-brand hover:text-void hover:border-brand transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden bg-surface">
          <div className="flex transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]" style={{ transform: `translateX(-${index * 100}%)` }}>
            {PHOTOS.map((p, i) => (
              <button
                key={i}
                onClick={() => setLightbox(true)}
                data-testid={`slideshow-slide-${i}`}
                aria-label="Enlarge photo"
                className="group relative w-full shrink-0 aspect-[4/3] sm:aspect-[16/9] cursor-zoom-in bg-void"
              >
                <img src={p.src} alt={p.alt} loading={i === 0 ? "eager" : "lazy"} decoding="async" className="w-full h-full object-contain" />
                <span className="absolute inset-0 bg-void/0 group-hover:bg-void/20 transition-colors" />
                <span className="absolute bottom-4 right-4 grid place-items-center w-11 h-11 bg-void/70 text-stark opacity-0 group-hover:opacity-100 transition-opacity">
                  <Expand className="w-5 h-5" />
                </span>
              </button>
            ))}
          </div>

          {/* Mobile arrows overlaid */}
          <button onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous photo"
            className="sm:hidden absolute left-2 top-1/2 -translate-y-1/2 grid place-items-center w-10 h-10 bg-void/70 text-stark">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next photo"
            className="sm:hidden absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center w-10 h-10 bg-void/70 text-stark">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {PHOTOS.map((_, i) => (
            <button key={i} onClick={() => setIndex(i)} aria-label={`Go to photo ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-brand" : "w-3 bg-hairline hover:bg-concrete"}`} />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div data-testid="lightbox" className="fixed inset-0 z-[70] bg-void/95 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLightbox(false)}>
          <button onClick={() => setLightbox(false)} data-testid="lightbox-close" aria-label="Close"
            className="absolute top-5 right-5 grid place-items-center w-12 h-12 border border-hairline text-stark hover:bg-brand hover:text-void hover:border-brand transition-colors">
            <X className="w-6 h-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" aria-label="Previous photo"
            className="absolute left-4 lg:left-8 grid place-items-center w-12 h-12 lg:w-14 lg:h-14 border border-hairline text-stark hover:bg-brand hover:text-void hover:border-brand transition-colors">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <figure className="max-w-[92vw] lg:max-w-[80vw]" onClick={(e) => e.stopPropagation()}>
            <img src={PHOTOS[index].src} alt={PHOTOS[index].alt} className="max-h-[82vh] w-auto mx-auto object-contain" />
            <figcaption className="text-center text-concrete text-sm mt-4 font-mono tracking-wide">
              {index + 1} / {n} — {PHOTOS[index].alt}
            </figcaption>
          </figure>
          <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" aria-label="Next photo"
            className="absolute right-4 lg:right-8 grid place-items-center w-12 h-12 lg:w-14 lg:h-14 border border-hairline text-stark hover:bg-brand hover:text-void hover:border-brand transition-colors">
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </section>
  );
};
